// @ts-nocheck
import cloneDeep from 'lodash/cloneDeep';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { performCalculation } from '../calculators/performCalculation';
import {
  QUERY_KEY_LOANS,
  QUERY_KEY_OWNER_CATEGORY,
  QUERY_KEY_OWN_PAYMENT,
  QUERY_KEY_PROPERTY_TYPE,
  QUERY_KEY_PROPERTY_VALUE,
  TABLE_FIELDKEYS_REALKREDIT_1,
  TABLE_FIELDKEYS_REALKREDIT_2,
} from '../config';
import { getInitialLoansFromQueryOrSettings } from '../helpers/initial-loans';
import { getQueryFloatOrNull, getQueryString } from '../helpers/query';
import { LoanStateItem } from '../types/LoanStateItem';
import { ViewType } from '../types/ViewType';

// Create contexts
const InputsContext = createContext<any>(null);
const LoansContext = createContext<any>(null);
const SettingsContext = createContext<any>(null);

// Provider component
export const LoanCalculatorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Settings state
  const [settings, setSettings] = useState<any>({});
  const [apiUrl, setApiUrl] = useState('');

  // Inputs state
  // Note: useState initial values must be SSR-safe (no window/document access).
  // Query params are read in useEffect below to avoid React #418 hydration mismatch.
  const [loanType, setLoanType] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [ownerCategory, setOwnerCategory] = useState('');
  const [propertyValue, setPropertyValue] = useState<number | null>(null);
  const [ownPayment, setOwnPayment] = useState<number | null>(null);
  const [viewType, setViewType] = useState<ViewType>('realkredit');
  const [showModal, setShowModal] = useState(false);
  const [activeLoan, setActiveLoan] = useState<null | LoanStateItem>(null);
  const [selectedLoan, setSelectedLoan] = useState<any>('');
  const [propertyCategory, setPropertyCategory] = useState<any>('');
  const [ownPaymentVisible, setOwnPaymentVisible] = useState(true);
  const [tableFieldSet1Keys, setTableFieldSet1Keys] = useState<any>(TABLE_FIELDKEYS_REALKREDIT_1);
  const [tableFieldSet2Keys, setTableFieldSet2Keys] = useState<any>(TABLE_FIELDKEYS_REALKREDIT_2);
  const [wantedLoanAmount, setWantedLoanAmount] = useState<number | null>(null);
  const [remainingDebt, setRemainingDebt] = useState<number | null>(null);
  const [hasError, setError] = useState(false);
  const [submitCounter, setSubmitCounter] = useState(0);
  const [recalculate, setRecalculate] = useState(false);
  const [loanChanged, setLoanChanged] = useState(false);

  // Loans state
  const [loans, setLoans] = useState<LoanStateItem[]>([]);
  const [lastCalculationDate, setLastCalculationDate] = useState('');

  // Read query params on client after mount to avoid SSR/hydration mismatch (React #418)
  React.useEffect(() => {
    const qLoanType = getQueryString(QUERY_KEY_LOANS);
    const qPropertyType = getQueryString(QUERY_KEY_PROPERTY_TYPE);
    const qOwnerCategory = getQueryString(QUERY_KEY_OWNER_CATEGORY);
    const qPropertyValue = getQueryFloatOrNull(QUERY_KEY_PROPERTY_VALUE);
    const qOwnPayment = getQueryFloatOrNull(QUERY_KEY_OWN_PAYMENT);
    if (qLoanType) setLoanType(qLoanType);
    if (qPropertyType) setPropertyType(qPropertyType);
    if (qOwnerCategory) setOwnerCategory(qOwnerCategory);
    if (qPropertyValue !== null) setPropertyValue(qPropertyValue);
    if (qOwnPayment !== null) setOwnPayment(qOwnPayment);
  }, []);

  // Helper function to access current state values in callbacks
  const getInputFields = useCallback(
    () => ({
      propertyType,
      ownerCategory,
      propertyValue,
      wantedLoanAmount,
      ownPayment,
      remainingDebt,
      propertyCategory,
    }),
    [propertyType, ownerCategory, propertyValue, wantedLoanAmount, ownPayment, remainingDebt, propertyCategory]
  );

  // Loans methods
  const addLoan = useCallback(() => {
    setLoans((loans) => [...loans, { type: '', status: 'initial' }]);
  }, []);

  const removeLoan = useCallback((loanIndex: number) => {
    setLoans((loans) => loans.filter((_, index) => index !== loanIndex));
  }, []);

  const setLoan = useCallback((loanIndex: number, data: LoanStateItem) => {
    setLoans((loans) => loans.map((loan, index) => (index === loanIndex ? data : loan)));
  }, []);

  const doCalculation = useCallback(
    async (loanIndex: number, loanData: LoanStateItem) => {
      const inputFields = getInputFields();

      if (
        inputFields.propertyValue === null ||
        inputFields.ownPayment === null ||
        inputFields.wantedLoanAmount === null ||
        inputFields.remainingDebt === null
      ) {
        setLoan(loanIndex, {
          ...loanData,
          status: 'initial',
        });
        return;
      }

      setLoan(loanIndex, {
        ...loanData,
        status: 'loading',
      });

      const result = await performCalculation(loanData, inputFields, apiUrl);

      if (result !== false) {
        setLoan(loanIndex, {
          ...result,
          status: 'done',
        });
      } else {
        setLoan(loanIndex, {
          ...loanData,
          status: 'error',
        });
      }
    },
    [getInputFields, apiUrl, setLoan]
  );

  const calculateSingleLoan = useCallback(
    async (loanIndex: number) => {
      const loanData = cloneDeep(loans[loanIndex] || {});
      await doCalculation(loanIndex, loanData);
      setLastCalculationDate(Date.now().toString());
    },
    [doCalculation, loans]
  );

  const calculateLoansFromScratch = useCallback(async () => {
    await Promise.all(
      loans.map(async (loan, loanIndex) => {
        if (!loan.type) {
          return;
        }

        const dataFromSettings = cloneDeep(settings.loans[loan.type] || {});
        const loanData = {
          type: loan.type,
          ...dataFromSettings,
        };

        await doCalculation(loanIndex, loanData);
      })
    );

    setLastCalculationDate(Date.now().toString());
  }, [doCalculation, loans, settings.loans]);

  const setLoanTypeAndCalculate = useCallback(
    async (loanIndex: number, type: string) => {
      if (!type) {
        setLoan(loanIndex, {
          type,
          status: 'initial',
        });
        return;
      }

      const dataFromSettings = cloneDeep(settings.loans[type] || {});
      const loanData = {
        type,
        ...dataFromSettings,
      };

      await doCalculation(loanIndex, loanData);
      setLastCalculationDate(Date.now().toString());
    },
    [setLoan, doCalculation, settings.loans]
  );

  const setLoanPayloadAndCalculate = useCallback(
    async (loanIndex: number, payload: any, settingsParam?: any, tableItems?: any, isStatic?: boolean) => {
      const loanFromArray = cloneDeep(loans[loanIndex] || {});
      let loanData = {
        ...loanFromArray,
        payload,
      };

      if (settingsParam) {
        loanData = {
          ...loanFromArray,
          payload,
          settings: settingsParam,
        };
      }

      if (isStatic) {
        loanData = {
          ...loanFromArray,
          payload,
          settings: settingsParam,
          tableItems,
        };
        setLoan(loanIndex, {
          ...loanData,
          status: 'done',
        });
      } else {
        await doCalculation(loanIndex, loanData);
        setLastCalculationDate(Date.now().toString());
      }
    },
    [doCalculation, loans, setLoan]
  );

  const setInitialLoansFromSettings = useCallback((settingsParam: any) => {
    const initialLoans = getInitialLoansFromQueryOrSettings(settingsParam);
    setLoans(
      initialLoans.map((type) => {
        const dataFromSettings = cloneDeep(settingsParam.loans[type] || {});
        return {
          ...dataFromSettings,
          type,
          status: 'initial',
        };
      })
    );
  }, []);

  const setLoansAfterChange = useCallback((settingsParam, selectedLoan) => {
    if (settingsParam.loans && Object.keys(settingsParam).length > 0) {
      const defaultLoans = settingsParam?.table?.defaultLoans[selectedLoan];
      setLoans(
        defaultLoans.map((type) => {
          const dataFromSettings = cloneDeep(settingsParam.loans[type] || {});
          return {
            ...dataFromSettings,
            type,
            status: 'initial',
          };
        })
      );
    }
  }, []);

  const settingsValue = {
    settings,
    apiUrl,
    setSettings,
    setApiUrl,
  };

  const inputsValue = {
    loanType,
    propertyType,
    ownerCategory,
    propertyValue,
    wantedLoanAmount,
    ownPayment,
    viewType,
    showModal,
    activeLoan,
    selectedLoan,
    loanChanged,
    propertyCategory,
    ownPaymentVisible,
    tableFieldSet1Keys,
    tableFieldSet2Keys,
    remainingDebt,
    hasError,
    submitCounter,
    recalculate,
    setRecalculate,
    setLoanChanged,
    setSubmitCounter,
    setError,
    setLoanType,
    setPropertyType,
    setOwnerCategory,
    setPropertyValue,
    setWantedLoanAmount,
    setOwnPayment,
    setRemainingDebt,
    setViewType,
    setShowModal,
    setActiveLoan,
    setSelectedLoan,
    setPropertyCategory,
    setOwnPaymentVisible,
    setTableFieldSet1Keys,
    setTableFieldSet2Keys,
  };

  const loansValue = {
    loans,
    lastCalculationDate,
    loansCount: loans.length,
    addLoan,
    removeLoan,
    setLoan,
    setLoans,
    calculateSingleLoan,
    calculateLoansFromScratch,
    setLoanTypeAndCalculate,
    setLoanPayloadAndCalculate,
    setInitialLoansFromSettings,
    setLoansAfterChange,
  };

  return (
    <SettingsContext.Provider value={settingsValue}>
      <InputsContext.Provider value={inputsValue}>
        <LoansContext.Provider value={loansValue}>{children}</LoansContext.Provider>
      </InputsContext.Provider>
    </SettingsContext.Provider>
  );
};

// Custom hooks
export const useSettingsState = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettingsState must be used within LoanCalculatorProvider');
  }
  return context;
};

export const useInputsState = () => {
  const context = useContext(InputsContext);
  if (!context) {
    throw new Error('useInputsState must be used within LoanCalculatorProvider');
  }
  return context;
};

export const useLoansState = () => {
  const context = useContext(LoansContext);
  if (!context) {
    throw new Error('useLoansState must be used within LoanCalculatorProvider');
  }
  return context;
};
